/**
 * AIThinkingTimeline — Step-by-step progress timeline for the AI's
 * planning / execution pipeline.
 *
 * Renders a vertical, animated checklist of the steps Lina is taking
 * while preparing her response (Understanding → Searching → Planning →
 * Executing actions → Generating). Each step shows an icon, its label,
 * and its current state (active spinner, checkmark for done, error).
 *
 * Designed to make the "thinking" phase feel transparent and engaging
 * instead of a generic loading spinner.
 */

import React, { useEffect, useState } from 'react';
import { cn } from '../../utils/cn';

/* ── Step → icon + accent metadata ─────────────────────────────────────── */
const STEP_META = {
  understanding: {
    label: 'Understanding',
    accent: 'sky',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
  },
  searching: {
    label: 'Gathering context',
    accent: 'indigo',
    icon: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
      </>
    ),
  },
  thinking: {
    label: 'Analyzing',
    accent: 'violet',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v3m0 6v3m6-9h-3m-6 0H6m10.243-4.243l-2.121 2.121M9.879 14.121l-2.122 2.122m0-10.486l2.122 2.121m4.243 4.243l2.121 2.121" />
    ),
  },
  planning: {
    label: 'Planning steps',
    accent: 'amber',
    icon: (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5a2 2 0 012-2h2a2 2 0 012 2v0a2 2 0 01-2 2h-2a2 2 0 01-2-2v0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
      </>
    ),
  },
  executing: {
    label: 'Executing',
    accent: 'emerald',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    ),
  },
  generating: {
    label: 'Writing response',
    accent: 'blue',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
    ),
  },
  retrying: {
    label: 'Retrying',
    accent: 'orange',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
    ),
  },
};

const ACCENT_CLASSES = {
  sky:     { ring: 'ring-sky-500/30',     bg: 'bg-sky-500/10',     text: 'text-sky-600 dark:text-sky-300',     dot: 'bg-sky-500' },
  indigo:  { ring: 'ring-indigo-500/30',  bg: 'bg-indigo-500/10',  text: 'text-indigo-600 dark:text-indigo-300', dot: 'bg-indigo-500' },
  violet:  { ring: 'ring-violet-500/30',  bg: 'bg-violet-500/10',  text: 'text-violet-600 dark:text-violet-300', dot: 'bg-violet-500' },
  amber:   { ring: 'ring-amber-500/30',   bg: 'bg-amber-500/10',   text: 'text-amber-600 dark:text-amber-300',   dot: 'bg-amber-500' },
  emerald: { ring: 'ring-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-300', dot: 'bg-emerald-500' },
  blue:    { ring: 'ring-blue-500/30',    bg: 'bg-blue-500/10',    text: 'text-blue-600 dark:text-blue-300',    dot: 'bg-blue-500' },
  orange:  { ring: 'ring-orange-500/30',  bg: 'bg-orange-500/10',  text: 'text-orange-600 dark:text-orange-300', dot: 'bg-orange-500' },
  gray:    { ring: 'ring-gray-300/40',    bg: 'bg-gray-200/50 dark:bg-gray-700/40', text: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
};

/* ── Tiny live timer for the active step ───────────────────────────────── */
function ElapsedBadge({ startedAt, active }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => force((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [active]);
  if (!startedAt) return null;
  const ms = Date.now() - startedAt;
  if (ms < 250) return null;
  const seconds = (ms / 1000).toFixed(ms < 10_000 ? 1 : 0);
  return (
    <span className="text-[10px] font-mono tabular-nums text-gray-400 dark:text-gray-500">
      {seconds}s
    </span>
  );
}

/* ── Single step row ───────────────────────────────────────────────────── */
function StepRow({ step, isLast }) {
  const meta = STEP_META[step.key] || {
    label: step.text || 'Working',
    accent: 'gray',
    icon: <circle cx="12" cy="12" r="3" />,
  };
  const isActive = step.status === 'active';
  const isDone = step.status === 'done';
  const isError = step.status === 'error';
  const accent = ACCENT_CLASSES[isError ? 'orange' : isActive ? meta.accent : isDone ? 'emerald' : 'gray'];

  return (
    <li className="relative flex gap-2.5 items-start">
      {/* connector line */}
      {!isLast && (
        <span
          aria-hidden="true"
          className={cn(
            'absolute left-[11px] top-6 w-px h-[calc(100%-4px)]',
            isDone ? 'bg-emerald-300/60 dark:bg-emerald-700/50' : 'bg-gray-200 dark:bg-gray-700'
          )}
        />
      )}

      {/* icon disk */}
      <div
        className={cn(
          'relative w-[22px] h-[22px] rounded-full flex items-center justify-center flex-shrink-0 ring-1 transition-colors',
          accent.bg,
          accent.ring,
          isActive && 'ring-2'
        )}
      >
        {isActive && (
          <span
            aria-hidden="true"
            className={cn('absolute inset-0 rounded-full animate-ping opacity-40', accent.dot)}
          />
        )}
        {isDone ? (
          <svg className="w-3 h-3 text-emerald-600 dark:text-emerald-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        ) : isError ? (
          <svg className="w-3 h-3 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 3h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        ) : (
          <svg
            className={cn('w-3 h-3', accent.text)}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            {meta.icon}
          </svg>
        )}
      </div>

      {/* label + detail text */}
      <div className="flex-1 min-w-0 pb-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-[11.5px] font-semibold leading-none',
              isActive ? accent.text : isDone ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'
            )}
          >
            {meta.label}
          </span>
          <ElapsedBadge startedAt={step.startedAt} active={isActive} />
        </div>
        {step.text && step.text !== meta.label && (
          <p
            className={cn(
              'text-[11px] mt-0.5 leading-snug truncate',
              isActive
                ? 'text-gray-600 dark:text-gray-300'
                : 'text-gray-400 dark:text-gray-500'
            )}
            title={step.text}
          >
            {step.text}
          </p>
        )}
      </div>
    </li>
  );
}

/* ── Container ─────────────────────────────────────────────────────────── */
export default function AIThinkingTimeline({ steps = [], compact = false }) {
  if (!steps.length) {
    // Initial bouncing-dots placeholder while we wait for the first status
    return (
      <div className="flex items-center gap-2 mt-1">
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800/80 dark:to-gray-800/60 border border-blue-100/60 dark:border-gray-700/60">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 animate-bounce [animation-delay:0ms]" />
          <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 dark:bg-indigo-500 animate-bounce [animation-delay:150ms]" />
          <div className="w-1.5 h-1.5 rounded-full bg-violet-400 dark:bg-violet-500 animate-bounce [animation-delay:300ms]" />
          <span className="ml-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
            Connecting…
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mt-2 rounded-xl border border-gray-200/70 dark:border-gray-700/60',
        'bg-gradient-to-br from-gray-50/80 to-white dark:from-gray-900/60 dark:to-gray-900/30',
        'shadow-sm overflow-hidden animate-slide-in-from-top-1',
        compact ? 'px-3 py-2' : 'px-3.5 py-2.5'
      )}
      role="status"
      aria-live="polite"
      aria-label="AI assistant progress"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Thinking
        </p>
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500" />
          </span>
          <span className="text-[10.5px] font-medium text-blue-600 dark:text-blue-300">
            in progress
          </span>
        </div>
      </div>
      <ol className="space-y-0">
        {steps.map((step, i) => (
          <StepRow key={step.id || i} step={step} isLast={i === steps.length - 1} />
        ))}
      </ol>
    </div>
  );
}
